import { FC, useCallback, useState, memo, useEffect, ChangeEvent } from 'react';
import { useAppSelector } from '@redux/hooks';
import { Input, TextArea } from '../Input/Input';
import { InputMask } from '../InputMask/InputMask';
import { FormFieldProps } from '../../Types';
import { Icons } from './Icons';
import { WrapperField } from './WrapperField';
import { Error } from './Error';
import { useIsMount } from '@hooks/useIsMount';
import { removeSpaces } from './helpers/removeSpaces';
import IconShow from '@public/next/ui-kit/input/show.svg';
import IconHide from '@public/next/ui-kit/input/hide.svg';
import classNames from 'classnames';

import styles from './FormField.module.scss';

export const FormField: FC<FormFieldProps> = memo(function FormField(props) {
  const {
    id,
    isRequired,
    classNameContainer,
    className,
    error,
    maxLength,
    label,
    name,
    mask,
    register,
    type,
    rows,
    isValid,
    isBlock,
    isFetching,
    placeholder,
    helperMessage,
    value,
    limit,
    listData,
    setValue,
    getStatusField,
    isRemovingSpaces,
    handleClickValue,
    dataTestId,
    isDisable,
    autoComplete,
    autoFocus,
    onChange,
    onBlur,
  } = props;
  const [focus, setFocus] = useState<boolean>(false);
  const { isIos } = useAppSelector(state => state.header.data.userAgent);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const showSelectItem = !!listData?.length && focus;
  const isMount = useIsMount();

  const handleFocus = useCallback(() => {
    setTimeout(() => {
      setFocus(prevState => !prevState);
    }, 200);
  }, []);

  const handleBlur = useCallback(e => {
    onBlur && onBlur(e);
    setTimeout(() => {
      setFocus(prevState => !prevState);
    }, 200);
  }, []);

  const handleShowPassword = useCallback(() => {
    setShowPassword(prevState => !prevState);
  }, []);

  const handleClearField = useCallback(() => {
    setValue(name, '', { shouldValidate: true });
  }, []);

  useEffect(() => {
    if (isMount && getStatusField) {
      getStatusField(focus, !!error);
    }
  }, [isMount, focus, error]);

  const handlerOnlyNumber = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    event.target.value = event.target.value.replace(/(?![0-9])./gim, '');
    handlerValue(event);
  };

  const handlerValue = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const valueElement = event.target.value;
    event.target.value = isRemovingSpaces ? removeSpaces(valueElement) : valueElement;
    setValue(event.target.name, event.target.value);
    onChange && onChange(event.target.value);
  };

  return (
    <div className={classNames(styles.Wrapper, classNameContainer, { [styles.IsIos]: isIos })}>
      {label && (
        <label>
          {label}
          {helperMessage && <div className={styles.helperMessage}>{helperMessage}</div>}
        </label>
      )}
      {type === 'text' && (
        <WrapperField isFocus={focus} error={error}>
          <Input
            id={id}
            className={className}
            name={name}
            type={type}
            maxLength={maxLength}
            {...register(name, { onChange: handlerValue })}
            placeholder={placeholder}
            onFocus={handleFocus}
            onBlur={handleBlur}
            data-testid={dataTestId}
            disabled={isDisable}
            autoFocus={autoFocus}
          />
          <Icons isFocus={focus} value={value} isValid={isValid} isRequired={isRequired} handleClearField={handleClearField} />
        </WrapperField>
      )}
      {type === 'number' && (
        <WrapperField isFocus={focus} error={error}>
          <Input
            id={id}
            className={className}
            name={name}
            type={type}
            disabled={isDisable}
            maxLength={maxLength}
            {...register(name, { onChange: handlerValue })}
            placeholder={placeholder}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          <Icons isFocus={focus} value={value} isValid={isValid} isRequired={isRequired} handleClearField={handleClearField} />
        </WrapperField>
      )}
      {type === 'tel' && (
        <WrapperField isFocus={focus} error={error}>
          <InputMask
            id={id}
            className={className}
            name={name}
            type={type}
            disabled={isDisable}
            {...register(name, { onChange: handlerValue })}
            placeholder={placeholder}
            mask={mask}
            onFocus={handleFocus}
            onBlur={handleBlur}
            data-testid={dataTestId}
            autoComplete={autoComplete}
          />
          <Icons
            isBlock={isBlock}
            isFocus={focus}
            value={value}
            isValid={isValid}
            isRequired={isRequired}
            handleClearField={handleClearField}
          />
        </WrapperField>
      )}
      {type === 'password' && (
        <div className={styles.Box}>
          <WrapperField isFocus={focus} error={error}>
            <Input
              id={id}
              className={className}
              name={name}
              type={showPassword ? 'text' : type}
              disabled={isDisable}
              {...register(name, { onChange: handlerValue })}
              placeholder={placeholder}
              onFocus={handleFocus}
              onBlur={handleBlur}
              data-testid={dataTestId}
            />
            <Icons isFocus={focus} value={value} isValid={isValid} isRequired={isRequired} handleClearField={handleClearField} />
          </WrapperField>
          <div className={styles.ShowPassword}>
            {!showPassword ? <IconShow onClick={handleShowPassword} /> : <IconHide onClick={handleShowPassword} />}
          </div>
        </div>
      )}
      {type === 'textarea' && (
        <WrapperField isFocus={focus} error={error} type={type}>
          <TextArea
            id={id}
            className={className}
            name={name}
            {...register(name, { onChange: handlerValue })}
            placeholder={placeholder}
            onFocus={handleFocus}
            onBlur={handleBlur}
            limit={limit}
            disabled={isDisable}
            rows={rows}
            data-testid={dataTestId}
          />
          <Icons isFocus={focus} value={value} isValid={isValid} isRequired={isRequired} limit={limit} handleClearField={handleClearField} />
        </WrapperField>
      )}
      {type === 'select' && (
        <>
          <WrapperField isFocus={focus} error={error} showSelectItem={showSelectItem}>
            <Input
              id={id}
              className={className}
              name={name}
              type={type}
              disabled={isDisable}
              {...register(name, { onChange: handlerValue })}
              placeholder={placeholder}
              onFocus={handleFocus}
              onBlur={handleBlur}
              data-testid={dataTestId}
            />
            <Icons
              isFocus={focus}
              value={value}
              isValid={isValid}
              isRequired={isRequired}
              isFetching={isFetching}
              handleClearField={handleClearField}
            />
          </WrapperField>
          {showSelectItem && !error && (
            <div className={styles.SelectWrapper}>
              {listData.map(item => (
                <div className={styles.SelectItem} onClick={() => handleClickValue(item)} key={item}>
                  {item}
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {type === 'onlyNumbers' && (
        <WrapperField isFocus={focus} error={error}>
          <Input
            id={id}
            className={className}
            name={name}
            inputMode="numeric"
            type={'text'}
            maxLength={maxLength}
            disabled={isDisable}
            {...register(name, { onChange: handlerOnlyNumber })}
            placeholder={placeholder}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          <Icons
            isFocus={focus}
            value={value}
            isValid={isValid}
            isRequired={isRequired}
            isFetching={isFetching}
            handleClearField={handleClearField}
          />
        </WrapperField>
      )}
      <Error error={error} />
    </div>
  );
});
